import React, { useState, useEffect } from 'react';
import { useToast } from '../../context/ToastContext';
import { Card, Badge, SectionHeader, Spinner } from '../../components/shared/UI';
import API from '../../utils/apiClient';

export default function StudentPruefungstrainingPage() {
  const { addToast } = useToast();
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [levelFilter, setLevel]   = useState('all');

  useEffect(() => {
    API.get('/pruefungstraining')
      .then(({ data }) => setMaterials(data.materials || []))
      .catch(() => addToast('Failed to load materials', 'error'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = levelFilter === 'all' ? materials : materials.filter(m => m.level === levelFilter);
  const levels   = [...new Set(materials.map(m => m.level))].sort();

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <SectionHeader
        title="Prüfungstraining"
        subtitle="Practice exam materials to prepare for your Goethe certificate"
      />

      {/* Level filter */}
      <div className="flex gap-1 p-1 bg-surface rounded-xl mb-6 overflow-x-auto">
        {['all', ...['A1','A2','B1','B2'].filter(l => levels.includes(l))].map(tab => (
          <button key={tab} onClick={() => setLevel(tab)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all whitespace-nowrap
              ${levelFilter === tab ? 'bg-white text-brand-500 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {tab === 'all' ? '📚 All Levels' : `Level ${tab}`}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Spinner size="lg" className="text-brand-500" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-4xl mb-3">📄</p>
          <p className="text-base font-bold text-de-black">No practice materials yet</p>
          <p className="text-sm text-gray-500">Your teacher hasn't uploaded any exam practice files yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {filtered.map(m => (
            <Card key={m.id} className="p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start gap-4">
                {/* PDF icon */}
                <div className="w-12 h-14 rounded-lg bg-brand-50 border border-brand-200 flex flex-col items-center justify-center flex-shrink-0">
                  <span className="text-brand-500 font-bold text-xs uppercase">{m.file_type?.replace('application/', '') || 'PDF'}</span>
                  <span className="text-brand-500 text-2xl leading-none">📄</span>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="gold" size="xs">{m.level}</Badge>
                    <span className="text-xs text-gray-400">
                      {new Date(m.created_at).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' })}
                    </span>
                  </div>
                  <h3 className="font-bold text-de-black text-sm">{m.title}</h3>
                  {m.description && (
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">{m.description}</p>
                  )}
                </div>
              </div>

              <div className="mt-4 pt-3 border-t border-gray-100">
                <a href={m.file_url} target="_blank" rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-brand-500 text-white text-sm font-bold hover:bg-brand-600 transition-colors">
                  ↓ Download Practice Exam
                </a>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
